/**
 * Filtros avanzados de la lista de ventas (los del panel lateral).
 *
 * Viven en un solo objeto, `advanced`, para que el panel los edite como
 * borrador y ListaVenta los reciba de una vez: un cambio = una consulta.
 * La forma del objeto y los nombres de los params coinciden con
 * `VentaFilterDTO` del backend.
 *
 * Sin dependencias de Vue: se prueba aparte (tests/unit/views/ventaFilters.spec.js).
 */

export const STATUS_VIEWS = [
    { value: 'active', label: 'Activas' },
    { value: 'cancelled', label: 'Canceladas' },
    { value: 'all', label: 'Todas' }
];

// `key` es el prefijo del param (saldoMin, totalMax) y de la clave en `advanced.ranges`.
// Saldo y total responden preguntas reales (¿quién debe?, ¿de cuánto son las ventas?).
// Las demás columnas numéricas se resuelven ordenando.
export const RANGE_FIELDS = [
    { key: 'saldo', label: 'Saldo' },
    { key: 'total', label: 'Total' }
];

export const emptyAdvanced = () => ({
    clientId: null,
    sucursalId: null,
    typeOutIds: [],
    creditOnly: false,
    status: 'active',
    ranges: RANGE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: { min: '', max: '' } }), {})
});

// Copia profunda: el panel edita un borrador, no el objeto aplicado.
export const cloneAdvanced = (advanced) => ({
    ...advanced,
    typeOutIds: [...advanced.typeOutIds],
    ranges: RANGE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: { ...advanced.ranges[key] } }), {})
});

const hasValue = (value) => value !== '' && value !== null && value !== undefined;

export const hasRange = (range) => !!range && (hasValue(range.min) || hasValue(range.max));

// Mínimo mayor que el máximo. El backend también lo rechaza, pero aquí se avisa antes de enviar.
export const isRangeInverted = (range) =>
    !!range && hasValue(range.min) && hasValue(range.max) && Number(range.min) > Number(range.max);

export const hasInvertedRange = (advanced) => RANGE_FIELDS.some(({ key }) => isRangeInverted(advanced.ranges[key]));

/**
 * Params que se mezclan en `tableFilters`. Lo vacío va como undefined: axios no
 * lo manda y el backend usa sus defaults. La lista de tipos va separada por
 * comas porque axios serializaría el arreglo como `typeOutId[]=1`.
 */
export const advancedParams = (advanced) => {
    const params = {
        clientId: advanced.clientId || undefined,
        sucursalId: advanced.sucursalId || undefined,
        typeOutId: advanced.typeOutIds.length ? advanced.typeOutIds.join(',') : undefined,
        optCreditFilter: advanced.creditOnly ? true : undefined,
        status: advanced.status !== 'active' ? advanced.status : undefined
    };
    RANGE_FIELDS.forEach(({ key }) => {
        const { min, max } = advanced.ranges[key];
        params[`${key}Min`] = hasValue(min) ? min : undefined;
        params[`${key}Max`] = hasValue(max) ? max : undefined;
    });
    return params;
};

const rangeLabel = (label, { min, max }) => {
    if (hasValue(min) && hasValue(max)) return `${label}: ${min} – ${max}`;
    if (hasValue(min)) return `${label}: ≥ ${min}`;
    return `${label}: ≤ ${max}`;
};

/**
 * Chips de lo aplicado, con la `key` que `removeAdvanced` entiende.
 * `catalogs`: { clientes, sucursales, tipoSalida } para poner nombres, no ids.
 */
export const advancedChips = (advanced, catalogs = {}) => {
    // Cada catálogo tiene su propia llave: las sucursales y los tipos de salida
    // usan `id`, los clientes usan `idCliente`.
    const nameOf = (list = [], id, { idField = 'id', nameField = 'nombre' } = {}) => {
        const found = list.find((item) => item[idField] === id);
        return found ? found[nameField] : id;
    };
    const chips = [];
    if (advanced.clientId) {
        chips.push({
            key: 'advClient',
            icon: 'fas fa-user',
            label: `Cliente: ${nameOf(catalogs.clientes, advanced.clientId, { idField: 'idCliente', nameField: 'nombreComercial' })}`
        });
    }
    if (advanced.sucursalId) {
        chips.push({ key: 'sucursal', icon: 'fas fa-store-alt', label: `Sucursal: ${nameOf(catalogs.sucursales, advanced.sucursalId)}` });
    }
    if (advanced.typeOutIds.length) {
        const names = advanced.typeOutIds.map((id) => nameOf(catalogs.tipoSalida, id)).join(', ');
        chips.push({ key: 'types', icon: 'fas fa-tags', label: `Tipo: ${names}` });
    }
    if (advanced.creditOnly) {
        chips.push({ key: 'credit', icon: 'fas fa-file-invoice-dollar', label: 'Solo con saldo' });
    }
    if (advanced.status !== 'active') {
        const view = STATUS_VIEWS.find((v) => v.value === advanced.status);
        chips.push({ key: 'status', icon: 'fas fa-toggle-on', label: `Estado: ${view ? view.label : advanced.status}` });
    }
    RANGE_FIELDS.forEach(({ key, label }) => {
        if (hasRange(advanced.ranges[key])) {
            chips.push({ key: `range:${key}`, icon: 'fas fa-sliders-h', label: rangeLabel(label, advanced.ranges[key]) });
        }
    });
    return chips;
};

// Cuántos filtros del panel hay activos (el contador del icono). "Activas" es el valor por defecto, no cuenta.
export const countAdvanced = (advanced) => advancedChips(advanced).length;

// Devuelve un `advanced` nuevo sin el filtro del chip `key`.
export const removeAdvanced = (advanced, key) => {
    const next = cloneAdvanced(advanced);
    if (key === 'advClient') next.clientId = null;
    if (key === 'sucursal') next.sucursalId = null;
    if (key === 'types') next.typeOutIds = [];
    if (key === 'credit') next.creditOnly = false;
    if (key === 'status') next.status = 'active';
    if (key.startsWith('range:')) next.ranges[key.slice('range:'.length)] = { min: '', max: '' };
    return next;
};
